import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import {useApi} from '../../lib/api';
import Screen from '../../components/Screen';
import Content from '../../components/Content';
import Form from '../../components/Form';
import Input from '../../components/Input';
import TextArea from '../../components/TextArea';
import Button from '../../components/Button';
import Loading from '../../components/Loading';
import {ScalePrintTabs} from './ScalePrintTabs';

function PrintLayoutFormScreen() {
  const api = useApi();
  const history = useHistory();
  const {id} = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [validationEnabled, setValidationEnabled] = useState(false);

  useEffect(() => {
    if (!isEdit) return;

    let cancelled = false;
    setLoading(true);

    api.getLabelLayout(id)
      .then(layout => {
        if (cancelled) return;
        setName(layout.name || '');
        setBody(layout.zplBody || '');
      })
      .catch(() => {
        if (cancelled) return;
        NotificationManager.error('Unable to load print layout');
        history.push('/devices/print-layouts');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [id]);

  const nameError = !name.trim() ? 'A name is required' : null;
  const bodyError = !body.trim() ? 'A ZPL body is required' : null;
  const invalid = !!nameError || !!bodyError;

  const handleSave = async () => {
    if (invalid) {
      setValidationEnabled(true);
      return;
    }

    setSaving(true);

    try {
      if (isEdit) {
        await api.updateLabelLayout({id, name: name.trim(), zplBody: body});
        NotificationManager.success(`Updated "${name.trim()}"`);
      } else {
        await api.createLabelLayout({name: name.trim(), zplBody: body});
        NotificationManager.success(`Created "${name.trim()}"`);
      }
      history.push('/devices/print-layouts');
    } catch (e) {
      NotificationManager.error(isEdit ? 'Unable to update print layout' : 'Unable to create print layout');
      setSaving(false);
    }
  };

  const handleCancel = () => {
    history.push('/devices/print-layouts');
  };

  return (
    <Screen title="Devices" activeLink="Devices">
      <ScalePrintTabs />
      <Content>
        <Header>{isEdit ? 'Edit Print Layout' : 'Create Print Layout'}</Header>
        {loading ? (
          <Loading />
        ) : (
          <Form onSubmit={handleSave}>
            <Form.Group>
              <Input
                id="layout-name"
                label="Name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                invalid={validationEnabled && !!nameError}
                validationText={validationEnabled ? nameError : ''}
              />
            </Form.Group>
            <Hint style={{fontWeight: 'bold'}}>
              The ZPL body is considered to be in 203 DPI, create your labels based on this.
              Using a different sizing could result in misalignment of the label.
            </Hint>
            <Form.Group>
              <Label htmlFor="layout-body">ZPL Body</Label>
              <BodyTextArea
                id="layout-body"
                placeholder="Paste ZPL code here..."
                value={body}
                onChange={e => setBody(e.target.value)}
                invalid={validationEnabled && !!bodyError}
              />
              {validationEnabled && bodyError && <ValidationText>{bodyError}</ValidationText>}
              <Hint>
                Available references:
                <HintList>
                  <li>
                    Sheet setup answer: <code>{"{{ contextAnswers['QuestionName'] }}"}</code>
                  </li>
                  <li>
                    Catalog attribute:{' '}
                    <code>{"{{ contextAttributes['QuestionName']['attributeKey'] }}"}</code>
                  </li>
                  <li>
                    Scale reading: <code>{'{{ weight }}'}</code> and <code>{'{{ weightUnit }}'}</code>
                  </li>
                  <li>
                    Math Operations: <code> {"{{ math.expr(\"weight * contextAttributes['CatalogName']['pricePerKg']\") }} "}</code>
                    <li-item>
                      Operators: + - / *
                    </li-item>
                  </li>
                  <li>
                    Date Numeric: <code>{"{{ date.numeric(\"contextAnswers['Date']\") }}"}</code>
                  </li>
                  <li>
                    Square Formatted with SKU and Price: <code>{"{{ square.format(contextAttributes['CatalogName']['sku'], math.expr(\"weight * contextAttributes['CatalogName']['pricePerKg']\")) }}"}</code>
                    Utilize ^BU for the barcode!
                  </li>
                  <li>
                    GTIN barcode: <code> {"{{ gtin.format(contextAttributes['Catalog Test']['gtin'], contextDates['Production Date']) }}"} </code>
                  </li>
                  <li>
                    GS1 barcode: <code> {"{{ gtin.format(contextAttributes['Product']['GTIN'], contextDates['Production Start Time'], weight) }}"} adds weight to the regular gtin barcode </code>
                  </li>
                  <li>
                    ^FX CENTER_BARCODE and ^FX CENTER_BARCODE number.
                    Center barcode by itself defaults to 329 for barcode width, enter a value to override.
                    <ul>
                      <li> Example: ^FX CENTER_BARCODE 228</li>
                      <li> GS1-128 (^BC) with weight, 34-digit payload, module width 2: ^FX CENTER_BARCODE 466 </li>
                      <li> GS1-128 (^BC) without weight, 24-digit payload, module width 2: ^FX CENTER_BARCODE 356</li>
                      <li> Width in dots = 11 × (3 + ceil(digits ÷ 2)) + 13, times the ^BY module width</li>
                      <li> UPC-A (^BU), module width 2: ^FX CENTER_BARCODE 228</li>
                    </ul>
                  </li>
                </HintList>
                <HintNote>
                  Always use bracket-quote notation for keys e.g.{' '}
                  <code>{"{{ contextAnswers['Bird Type'] }}"}</code>
                </HintNote>
              </Hint>
            </Form.Group>
            <Form.Group>
              <Label>ZPL References</Label>
              <ReferenceList>
                <li>
                  <a href="https://labelary.com/viewer.html" target="_blank" rel="noopener noreferrer">
                    Labelary ZPL Viewer
                  </a>
                  <span> — preview ZPL code as a rendered label.</span>
                </li>
                <li>
                  <a href="https://support.zebra.com/article/000026336" target="_blank" rel="noopener noreferrer">
                    Zebra ZPL Documentation
                  </a>
                  <span> — official Zebra ZPL command reference.</span>
                </li>
              </ReferenceList>
            </Form.Group>
            <ButtonGroup>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Layout'}
              </Button>
              <Button secondary onClick={handleCancel} disabled={saving}>Cancel</Button>
            </ButtonGroup>
          </Form>
        )}
      </Content>
    </Screen>
  );
}

const Header = styled.h2`
  margin: 0 0 1.5rem 0;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
`;

const BodyTextArea = styled(TextArea)`
  height: 20rem;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.875rem;
`;

const ValidationText = styled.div`
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const Hint = styled.div`
  font-size: 0.8rem;
  color: #747474;
  margin-top: 0.5rem;

  code {
    background: #f3f3f5;
    padding: 0.05rem 0.3rem;
    border-radius: 0.2rem;
  }
`;

const HintNote = styled.div`
  margin-top: 0.5rem;
`;

const HintList = styled.ul`
  margin: 0.25rem 0 0 0;
  padding-left: 1.25rem;

  li {
    margin-bottom: 0.15rem;
  }
`;

const ReferenceList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.875rem;
  line-height: 1.5;

  a {
    color: #0066cc;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  span {
    color: #747474;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

export default PrintLayoutFormScreen;